require('./bootstrap');

import { App as InertiaApp, plugin as InertiaPlugin } from '@inertiajs/inertia-vue3';
import { BRAND_ACCENT } from './brand';
import { bindHashLinks } from './smoothScroll';
import { createApp, h } from 'vue';
import { createI18n } from 'vue-i18n';
import { InertiaProgress } from '@inertiajs/progress';
import { initReveal } from './reveal';
import mixins from './mixins.js';
import { commonMessages } from './i18n/common';
import { bootAnalytics } from './analytics';
import { installLocalizedRoute } from './i18n/localeUrl';

/* Before any `route()` call from a page. The inline Ziggy script has already
   run, because this bundle is deferred. */
installLocalizedRoute();

const el = document.getElementById('app');

/* The page Inertia is booted with, parsed once and reused: the app needs it to
   mount, and the head handover below needs its `seo` prop to know whether there
   is a replacement to hand over to. */
const initialPage = JSON.parse(el.dataset.page);

/* The URL's language, before the first render. Defaulting to English and
   switching after mount is how a crawler that snapshots early would index the
   Malay URL as English. */
const bootLocale = (initialPage.props && initialPage.props.locale) || 'en-MY';

let appliedLocale = '';

function applyDocumentLocale(page) {
    const props = page && page.props;

    if (!props || !props.locale) {
        return;
    }

    const mark = props.locale + '|' + (props.htmlLang || '');

    if (mark === appliedLocale) {
        return;
    }

    appliedLocale = mark;
    i18n.global.locale.value = props.locale;

    if (props.htmlLang) {
        document.documentElement.lang = props.htmlLang;
    }
}

const i18n = createI18n({
    legacy: false,
    locale: bootLocale,
    fallbackLocale: 'en-MY',
    messages: commonMessages,
    /* The two warning switches, and why both are off.

       Every page and component still declares its own little dictionary through
       a local `useI18n({ messages })` scope, and those scopes fall back to this
       root composer for the shared strings. A local scope inherits `missingWarn`
       and `fallbackWarn` from the root, so both are set here, once.

       Left on, a local scope reports each shared key as missing before the root
       answers it, so every page load printed forty-odd `[intlify] Not found
       'Home' key in 'en' locale messages` lines: the same keys on the pricing
       table, the header, the footer and every FAQ band. That is now the normal
       path, not a fault, and the noise was drowning the real errors.

       A key that exists nowhere still renders its own English text, so a missing
       string shows up as untranslated copy rather than as a blank. */
    missingWarn: false,
    fallbackWarn: false,
});

applyDocumentLocale(initialPage);

const app = createApp({
    render: () =>
        h(InertiaApp, {
            initialPage,
            resolveComponent: (name) => require(`./Pages/${name}`).default,
        }),
})
    .mixin(mixins)
    .mixin({
        /* Before this component's render, so a client-side visit paints in the
           language of the URL it landed on. The first response is already set
           above, from the same prop. */
        beforeCreate() {
            if (this.$page) {
                applyDocumentLocale(this.$page);
            }
        },
    })
    .use(InertiaPlugin)
    .use(i18n);

app.config.globalProperties.$i18n = i18n;
app.mount(el);

bootAnalytics(initialPage);

/* Hand the document head over to Inertia.
 *
 * The first response carries the head `app/Support/Seo.php` resolved, written
 * into `app.blade.php` and marked with `data-seo`. It has to be there, because
 * the crawlers behind a shared link never run this file, so for them the
 * server-rendered tags are the only head the page has.
 *
 * Inertia's Head, by contrast, only manages the tags it created itself: it
 * identifies them by an `inertia` attribute, and it removes or replaces those on
 * every navigation. It knows nothing about tags it did not create, so the
 * server-rendered set would sit in the document forever and every page would
 * carry two `<title>` elements. The browser honours the first one, which means
 * the site would keep showing whatever the very first response said, on every
 * page, for the rest of the session.
 *
 * So the server's set is removed once, here, the moment Head has taken over.
 * That is already true by the time `mount` returns: `Head` renders during mount
 * and writes its tags to `document.head` as a side effect of that render, so
 * this runs after the replacement is in place and there is no gap for the head
 * to be empty in. Removing and re-adding happen in the same task, before the
 * browser paints.
 *
 * One case has no replacement to hand over to, and it is the reason this is a
 * conditional rather than a straight removal. A URL that matches no route is
 * rejected by the router before the middleware pipeline runs, so the `Error`
 * page Inertia renders for it arrives with no shared props and therefore no
 * `seo` prop, while the response the browser already has does carry the right
 * head: `app.blade.php` gets it from the view composer, which resolves the head
 * from the request directly rather than from a middleware. Removing the server's
 * tags there would leave the page with no title at all, and it would stay that
 * way for the rest of a client-side session. So the head is only handed over
 * when Inertia actually has one to hand it to; otherwise the server's stays, and
 * it is correct.
 *
 * Everything else in the head is left alone. The icons, the manifest, the CSRF
 * token and the analytics tags are not Inertia's to manage and survive the
 * session untouched. */
const serverHead = initialPage.props?.seo || null;

if (serverHead) {
    document.head.querySelectorAll('[data-seo]').forEach((tag) => tag.remove());
}

bindHashLinks();

/* Started after the mount so the library's first scan sees the rendered page
   rather than an empty #app. See reveal.js for the reveal's settings. */
initReveal();

/* The bar matches $secondary in _tokens.scss, the pink the site already uses
   for its accent rules, cursor and scroll-to-top button. SCSS variables do not
   reach a script, so the value is named once in brand.js and handed over from
   there. */
InertiaProgress.init({ color: BRAND_ACCENT });
